import { QuestionCircleOutlined } from "@ant-design/icons";
import {
	Button,
	Divider,
	Form,
	Input,
	Modal,
	message,
	Radio,
	Select,
	Switch,
	Tooltip,
} from "antd";
import { useEffect, useState } from "react";
import type { EventBookingScope } from "../../../shared/constants/event.constants";
import type {
	AdminEventOccasion,
	CreateOccasionPayload,
	OccasionImage,
} from "../../event-occasions/api/occasionApi";
import {
	useCreateOccasion,
	useOccasionDetail,
	useOccasionTemplates,
	useUpdateOccasion,
} from "../../event-occasions/hooks";
import {
	EventImageUpload,
	EventSettingsDetail,
	PublicStatus,
	RequiredBadge,
} from "../components/ui";
import type { CategoryMessageDetail } from "../types";

const { Option } = Select;
const { TextArea } = Input;

interface EventImage {
	file?: File;
	preview?: string;
	picUrl?: string;
	originalName?: string;
}

interface EventCreationModalProps {
	open: boolean;
	onClose: () => void;
	eventCategoryId?: number;
	currentEvent?: AdminEventOccasion;
	systemType?: string;
	primaryColor?: string;
	isPaymentAvailable?: boolean;
	uploadBaseUrl?: string;
	showCheckin?: boolean;
	showUniqueMessage?: boolean;
	showCancellationSettings?: boolean;
	showProgramSettings?: boolean;
}

const uploadImage = async (file: File): Promise<string> => {
	const formData = new FormData();
	formData.append("image", file);

	const response = await fetch("/api/upload/image", {
		method: "POST",
		credentials: "include",
		body: formData,
	});

	if (!response.ok) {
		const errorText = await response.text().catch(() => "Unknown error");
		throw new Error(`画像のアップロードに失敗しました: ${errorText}`);
	}

	const result = await response.json();
	if (!result.url) {
		throw new Error(
			"画像のアップロードに失敗しました: URLが返されませんでした",
		);
	}
	return result.url;
};

export const EventCreationModal = ({
	open,
	onClose,
	eventCategoryId = 1,
	currentEvent,
	systemType = "イベント",
	primaryColor = "#99CA29",
	isPaymentAvailable = true,
	uploadBaseUrl = "/uploads/occasions/",
	showCheckin = true,
	showUniqueMessage = true,
	showCancellationSettings = true,
	showProgramSettings = true,
}: EventCreationModalProps) => {
	const [form] = Form.useForm();
	const [occasionImages, setOccasionImages] = useState<EventImage[]>([{}]);
	const [selectedTemplateId, setSelectedTemplateId] = useState<
		number | undefined
	>(undefined);
	const [adminNotificationMemberIds, setAdminNotificationMemberIds] = useState<
		number[]
	>([]);
	const [memberCandidates, setMemberCandidates] = useState<
		{ memberId: number; displayName: string; picUrl?: string }[]
	>([]);
	const [isUploading, setIsUploading] = useState(false);

	useEffect(() => {
		if (!open) return;
		Promise.all([
			fetch("/api/m/spectators/candidates", { credentials: "include" }).then(
				(r) => r.json(),
			),
			fetch("/api/m/spectators", { credentials: "include" }).then((r) =>
				r.json(),
			),
		])
			.then(([candidatesRes, spectatorRes]) => {
				const candidates: {
					memberId: number;
					displayName: string;
					picUrl?: string;
				}[] = Array.isArray(candidatesRes)
					? candidatesRes
					: (candidatesRes?.data ?? []);
				const spectators: {
					memberId: number;
					displayName: string;
					picUrl?: string;
				}[] = (
					Array.isArray(spectatorRes)
						? spectatorRes
						: (spectatorRes?.data ?? [])
				).map(
					(s: {
						memberId: number;
						Member?: { displayName?: string; picUrl?: string };
					}) => ({
						memberId: s.memberId,
						displayName: s.Member?.displayName ?? `Member ${s.memberId}`,
						picUrl: s.Member?.picUrl,
					}),
				);
				const seen = new Set<number>();
				const merged = [...spectators, ...candidates].filter((m) => {
					if (seen.has(m.memberId)) return false;
					seen.add(m.memberId);
					return true;
				});
				setMemberCandidates(merged);
			})
			.catch(() => setMemberCandidates([]));
	}, [open]);

	const settingsHaveNonDefaultValues =
		open &&
		!!currentEvent &&
		(Boolean(currentEvent.isProgram) ||
			Boolean(currentEvent.isMessage) ||
			Boolean(currentEvent.cancelable) ||
			Boolean(currentEvent.notRegisterEventSameTime) ||
			(Number(currentEvent.fee ?? currentEvent.price) || 0) > 0);

	const { data: templates = [] } = useOccasionTemplates(open);
	const { data: templateDetail } = useOccasionDetail(selectedTemplateId);

	const createMutation = useCreateOccasion();
	const updateMutation = useUpdateOccasion();

	useEffect(() => {
		if (!open) {
			form.resetFields();
			setOccasionImages([{}]);
			setSelectedTemplateId(undefined);
			setAdminNotificationMemberIds([]);
			setIsUploading(false);
		}
	}, [open, form]);

	useEffect(() => {
		if (templateDetail?.occasion && open) {
			const occasion = templateDetail.occasion;

			const templateImages: EventImage[] = [];
			if (occasion.heroImageUrl) {
				templateImages.push({ picUrl: occasion.heroImageUrl });
			}
			if (occasion.gallery && Array.isArray(occasion.gallery)) {
				occasion.gallery.forEach((url: string) => {
					if (url && !templateImages.some((img) => img.picUrl === url)) {
						templateImages.push({ picUrl: url });
					}
				});
			}
			setOccasionImages(templateImages.length > 0 ? templateImages : [{}]);

			const isCancelable = Boolean(occasion.cancelable);
			const conditions = Array.isArray(occasion.cancelConditions)
				? occasion.cancelConditions
				: [];

			form.setFieldsValue({
				isDisplayed: Boolean(
					occasion.isDisplayed ?? occasion.isPublished ?? false,
				),
				title: occasion.title,
				subtitle: occasion.subtitle || "",
				description: occasion.description || "",
				fee: (occasion.fee ?? occasion.price) || 0,
				cancelable: isCancelable,
				cancelConditions: conditions,
				cancelDescription: occasion.cancelDescription || "",
				isMultiEvent: Boolean(
					occasion.isMultiEvent ?? occasion.allowMultipleBookings ?? true,
				),
				isMessage: Boolean(occasion.isMessage),
				isCheckin: Boolean(occasion.isCheckin),
				notRegisterEventSameTime: Boolean(occasion.notRegisterEventSameTime),
				isProgram: Boolean(occasion.isProgram),
				membershipScope: occasion.membershipScope ?? "member_only",
				messageRegisteredLineWorks:
					(occasion.categoryMessageDetail as CategoryMessageDetail)
						?.messageRegisteredLineWorks ||
					occasion.messageRegisteredLineWorks ||
					"",
				messageCanceledLineWorks:
					(occasion.categoryMessageDetail as CategoryMessageDetail)
						?.messageCanceledLineWorks ||
					occasion.messageCanceledLineWorks ||
					"",
				categoryMessageDetail:
					(occasion.categoryMessageDetail as CategoryMessageDetail) || {},
			});

			const memberIds = occasion.adminNotificationMemberIds as unknown as
				| number[]
				| null;
			setAdminNotificationMemberIds(Array.isArray(memberIds) ? memberIds : []);
		}
	}, [templateDetail, open, form]);

	useEffect(() => {
		if (open && currentEvent) {
			const existingImages: EventImage[] = [];

			if (currentEvent?.heroImageUrl) {
				existingImages.push({ picUrl: currentEvent.heroImageUrl });
			}

			if (currentEvent?.gallery && Array.isArray(currentEvent.gallery)) {
				currentEvent.gallery.forEach((url: string) => {
					if (url && !existingImages.some((img) => img.picUrl === url)) {
						existingImages.push({ picUrl: url });
					}
				});
			}

			if (existingImages.length === 0 && currentEvent?.occasionImages) {
				currentEvent.occasionImages.forEach((img: OccasionImage) => {
					existingImages.push({ picUrl: img?.picUrl });
				});
			}

			setOccasionImages(existingImages.length > 0 ? existingImages : [{}]);

			const isCancelable = Boolean(currentEvent?.cancelable);
			const conditions = Array.isArray(currentEvent?.cancelConditions)
				? currentEvent.cancelConditions
				: [];

			form.setFieldsValue({
				isDisplayed:
					currentEvent?.isDisplayed ?? currentEvent?.isPublished ?? false,
				title: currentEvent?.title,
				subtitle: currentEvent?.subtitle || "",
				description: currentEvent?.description || "",
				fee: currentEvent?.fee || currentEvent?.price || 0,
				cancelable: isCancelable,
				cancelConditions: conditions,
				cancelDescription: currentEvent?.cancelDescription || "",
				isMultiEvent:
					currentEvent?.isMultiEvent ??
					currentEvent?.allowMultipleBookings ??
					true,
				isMessage: currentEvent?.isMessage || false,
				isCheckin: currentEvent?.isCheckin || false,
				notRegisterEventSameTime:
					currentEvent?.notRegisterEventSameTime || false,
				isProgram: currentEvent?.isProgram || false,
				membershipScope: currentEvent?.membershipScope || "member_only",
				messageRegisteredLineWorks:
					(currentEvent?.categoryMessageDetail as CategoryMessageDetail)
						?.messageRegisteredLineWorks || "",
				messageCanceledLineWorks:
					(currentEvent?.categoryMessageDetail as CategoryMessageDetail)
						?.messageCanceledLineWorks || "",
				categoryMessageDetail:
					(currentEvent?.categoryMessageDetail as CategoryMessageDetail) || {},
			});

			const rawMemberIds = currentEvent?.adminNotificationMemberIds as unknown;
			let parsedMemberIds: number[] = [];
			if (Array.isArray(rawMemberIds)) {
				parsedMemberIds = rawMemberIds as number[];
			} else if (typeof rawMemberIds === "string") {
				try {
					parsedMemberIds = JSON.parse(rawMemberIds);
				} catch {
					/* ignore */
				}
			}
			setAdminNotificationMemberIds(parsedMemberIds);
		}
	}, [open, currentEvent, form]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();

			const imagesToUpload = occasionImages.filter(
				(img) => img.file || img.picUrl,
			);

			setIsUploading(true);

			const uploadedImageUrls: string[] = [];

			for (const img of imagesToUpload) {
				if (img.file && !img.picUrl) {
					try {
						const uploadedUrl = await uploadImage(img.file);
						uploadedImageUrls.push(uploadedUrl);
					} catch (uploadError) {
						setIsUploading(false);
						message.error(
							uploadError instanceof Error
								? uploadError.message
								: "画像のアップロードに失敗しました",
						);
						return;
					}
				} else if (img.picUrl) {
					uploadedImageUrls.push(img.picUrl);
				}
			}

			setIsUploading(false);

			const data = form.getFieldsValue();

			// Sanitize settings based on toggles
			const isMessage = Boolean(data?.isMessage);
			const cancelable = Boolean(data?.cancelable);
			const isMultiEvent = Boolean(data?.isMultiEvent);
			const feeValue = Number(data?.fee) || 0;
			const isPaidEvent = feeValue > 0;

			const payload: CreateOccasionPayload = {
				eventCategoryId: currentEvent?.eventCategoryId || eventCategoryId,
				title: (data?.title || "").trim(),
				subtitle: (data?.subtitle || "").trim() || null,
				description: (data?.description || "").trim() || null,
				isDisplayed: Boolean(data?.isDisplayed),
				isPublished: Boolean(data?.isDisplayed),
				fee: isPaidEvent ? feeValue : 0,
				isPaidEvent: isPaidEvent,
				price: isPaidEvent ? feeValue : null,
				cancelable: cancelable,
				isMessage: isMessage,
				isMultiEvent: isMultiEvent,
				allowMultipleBookings: isMultiEvent,
				notRegisterEventSameTime: isMultiEvent
					? Boolean(data?.notRegisterEventSameTime)
					: false,
				membershipScope: data?.membershipScope ?? "member_only",
				allowNonMember: data?.membershipScope === "member_and_guest",
				messageRegisteredLineWorks: data?.messageRegisteredLineWorks || null,
				messageCanceledLineWorks: data?.messageCanceledLineWorks || null,
				adminNotificationMemberIds: adminNotificationMemberIds,
				categoryMessageDetail: {
					...(isMessage ? data?.categoryMessageDetail : {}),
					messageRegisteredLineWorks: data?.messageRegisteredLineWorks || "",
					messageCanceledLineWorks: data?.messageCanceledLineWorks || "",
				},
				cancelConditions: cancelable ? data?.cancelConditions || [] : [],
				cancelDescription: cancelable ? data?.cancelDescription || null : null,
				heroImageUrl: uploadedImageUrls[0] || null,
				gallery: uploadedImageUrls,
			};

			if (currentEvent) {
				await updateMutation.mutateAsync({
					occasionId: (currentEvent.eventOccasionId ||
						currentEvent.occasionId) as number,
					payload,
				});
				message.success("イベントを更新しました");
			} else {
				await createMutation.mutateAsync(payload);
				message.success("イベントを作成しました");
			}

			onClose();
		} catch (error: unknown) {
			setIsUploading(false);
			console.error("Form submission error:", error);
			if (error instanceof Error) {
				message.error(error.message);
			}
		}
	};

	const handleTemplateChange = (occasionId: number) => {
		setSelectedTemplateId(occasionId);
	};

	const isLoading =
		createMutation.isPending || updateMutation.isPending || isUploading;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={currentEvent ? `${systemType}編集` : `新規${systemType}登録`}
			footer={null}
			maskClosable={false}
			centered
			width={720}
			styles={{
				body: {
					maxHeight: "85vh",
					overflowY: "auto",
					overflowX: "hidden",
					padding: "16px 24px",
				},
			}}
		>
			<Form
				size="large"
				form={form}
				layout="vertical"
				preserve={true}
				initialValues={{
					isDisplayed: false,
					isMultiEvent: true,
					notRegisterEventSameTime: false,
					fee: 0,
					cancelable: false,
					isMessage: true,
					categoryMessageDetail: {
						reminderMessageOneDay:
							"ご予約したイベントの前日となりました。気を付けてお越しください。",
					},
					isCheckin: false,
					membershipScope: "member_only" as EventBookingScope,
				}}
				requiredMark={false}
			>
				<div className="flex justify-end mb-4">
					<Select
						placeholder="既存のイベント情報を取得"
						value={selectedTemplateId}
						onChange={handleTemplateChange}
						className="w-[250px] text-center"
						allowClear
						onClear={() => setSelectedTemplateId(undefined)}
					>
						{templates.map((template) => (
							<Option
								key={template.eventOccasionId}
								value={template.eventOccasionId}
							>
								{`${template.title || "ー"}のテンプレート`}
							</Option>
						))}
					</Select>
				</div>

				<PublicStatus primaryColor={primaryColor} />

				<Form.Item
					name="membershipScope"
					label={
						<div className="flex items-center">
							<span className="font-bold">対象者</span>
							<Tooltip
								title="このイベントに予約できる対象者を選択してください。"
								placement="bottom"
							>
								<QuestionCircleOutlined
									className="ml-2"
									style={{ color: primaryColor }}
								/>
							</Tooltip>
						</div>
					}
				>
					<Radio.Group>
						<Radio value="member_only">会員のみ</Radio>
						<Radio value="member_and_guest">全員（会員・非会員）</Radio>
					</Radio.Group>
				</Form.Item>

				<Form.Item
					name="title"
					label={
						<div className="flex items-center">
							<span className="font-bold">{`${systemType}タイトル`}</span>
							<Tooltip
								title="最大255文字、一覧ページで表示できるのは約17文字です。"
								placement="bottom"
							>
								<QuestionCircleOutlined
									className="ml-2"
									style={{ color: primaryColor }}
								/>
							</Tooltip>
							<RequiredBadge />
						</div>
					}
					rules={[
						{
							required: true,
							whitespace: true,
							message: `${systemType}タイトルは必須です`,
						},
						{
							max: 255,
							message: `${systemType}タイトルは255文字以内にしてください`,
						},
					]}
				>
					<Input
						placeholder={`${systemType}タイトルを入力してください`}
						allowClear
					/>
				</Form.Item>

				<Form.Item
					name="subtitle"
					label={
						<div className="flex items-center">
							<span className="font-bold">{`${systemType}一覧リード文`}</span>
							<Tooltip
								title="イベント一覧ページで表示されるリード文です。"
								placement="bottom"
							>
								<QuestionCircleOutlined
									className="ml-2"
									style={{ color: primaryColor }}
								/>
							</Tooltip>
							<RequiredBadge />
						</div>
					}
					rules={[
						{
							required: true,
							whitespace: true,
							message: `${systemType}一覧リード文は必須です`,
						},
					]}
				>
					<TextArea placeholder="" autoSize={{ minRows: 3 }} />
				</Form.Item>

				<Divider className="my-4" />

				{showCheckin && (
					<div className="flex items-center gap-2 mb-4">
						<span>チェックイン機能</span>
						<Tooltip title="イベント当日のチェックイン機能を有効にします。">
							<QuestionCircleOutlined style={{ color: primaryColor }} />
						</Tooltip>
						<Form.Item noStyle name="isCheckin">
							<Switch />
						</Form.Item>
					</div>
				)}

				<EventSettingsDetail
					open={open}
					defaultExpanded={!!settingsHaveNonDefaultValues}
					isProgram={false}
					categoryId={eventCategoryId}
					primaryColor={primaryColor}
					isPaymentAvailable={isPaymentAvailable}
					showUniqueMessage={showUniqueMessage}
					showCancellationSettings={showCancellationSettings}
					showProgramSettings={showProgramSettings}
				/>

				<Form.Item
					name="description"
					label={<span className="font-bold">{`${systemType}内容`}</span>}
				>
					<TextArea placeholder="例：○○" autoSize={{ minRows: 3 }} />
				</Form.Item>

				<EventImageUpload
					value={occasionImages}
					onChange={setOccasionImages}
					systemType={systemType}
					primaryColor={primaryColor}
					uploadUrl={uploadBaseUrl}
				/>

				<div className="font-bold text-gray-800 mb-2">管理者通知設定</div>

				<div className="mb-4 text-[13px] text-gray-500">
					<p>
						※メッセージに[BUILDING]を入力すると、配信の時に予約のイベント名に変換されます。
					</p>
					<p>
						※メッセージに[NAME]を入力すると、配信の時に予約者の名前に変換されます。
					</p>
					<p>
						※メッセージに[DATE]を入力すると、配信の時に予約の日付に変換されます。
					</p>
					{/* <p>
						※メッセージに[TEL]を入力すると、配信の時に予約者の電話番号に変換されます。
					</p> */}
				</div>

				<Form.Item
					name="messageRegisteredLineWorks"
					label={
						<span className="font-bold">
							新規予約が登録されると送信されるメッセージ
						</span>
					}
					rules={[
						{ max: 500, message: "メッセージは500文字以内にしてください" },
					]}
				>
					<TextArea
						placeholder="メッセージを入力してください"
						autoSize={{ minRows: 1 }}
					/>
				</Form.Item>

				<Form.Item
					name="messageCanceledLineWorks"
					label={
						<span className="font-bold">
							予約がキャンセルされると送信されるメッセージ
						</span>
					}
					rules={[
						{ max: 500, message: "メッセージは500文字以内にしてください" },
					]}
				>
					<TextArea
						placeholder="メッセージを入力してください"
						autoSize={{ minRows: 1 }}
					/>
				</Form.Item>

				<Form.Item
					label={<span className="font-bold">管理者通知メンバー</span>}
					extra="予約・キャンセル時にLINEで通知するメンバーを選択してください"
				>
					<Select
						mode="multiple"
						placeholder="通知するメンバーを選択"
						value={adminNotificationMemberIds}
						onChange={(vals: number[]) => setAdminNotificationMemberIds(vals)}
						optionFilterProp="label"
						showSearch
						allowClear
						options={memberCandidates.map((m) => ({
							value: m.memberId,
							label: m.displayName,
						}))}
					/>
				</Form.Item>

				<Divider className="my-6" />

				<div className="flex justify-center">
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={isLoading}
						size="large"
						className="px-8 h-11"
						style={{
							backgroundColor: primaryColor,
							borderColor: primaryColor,
						}}
					>
						{isUploading
							? "画像アップロード中..."
							: currentEvent
								? "イベントを保存する"
								: "イベントを登録する"}
					</Button>
				</div>
			</Form>
		</Modal>
	);
};
